import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PermissionGate from './PermissionGate';
import {
  LayoutDashboard,
  ListOrdered,
  Package,
  Wallet,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  LogOut,
  GraduationCap,
  PieChart,
  Settings as SettingsIcon,
  Store,
  Users,
  Tag,
  TrendingUp,
  BarChart3,
  HelpCircle,
  MessageSquare,
  Image,
  Shield,
  Truck,
  ClipboardList,
  Star,
  Import,
  Megaphone,
  Bell,
  Calendar,
  CreditCard,
  FileText,
  Plug
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

/**
 * MobileNavigation Component
 * Mobile-optimized navigation with:
 * - Bottom navigation bar with 4 key items + hamburger
 * - Full-screen overlay menu with complete sidebar navigation
 */

const MobileNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isSuperAdmin } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  // Check if mobile - use window check for initial render
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1024;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  if (!isMobile) {
    return null;
  }

  // Hide bottom nav on form pages to avoid conflict with form's sticky footer
  const formPages = [
    '/create-listing',
    '/listings/products/',
    '/listings/services/',
    '/edit-listing',
    '/listings/edit'
  ];
  
  const isFormPage = formPages.some(page => location.pathname.includes(page));
  
  if (isFormPage) {
    return null; // Don't render bottom nav on form pages
  }

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  // Bottom navigation - 4 most important items + menu
  const bottomNavItems = [
    { icon: LayoutDashboard, label: 'Home', path: '/dashboard' },
    { icon: ListOrdered, label: 'Orders', path: '/orders' },
    { icon: Calendar, label: 'Bookings', path: '/bookings' },
    { icon: Package, label: 'Listings', path: '/listings' },
  ];

  // Admin navigation structure (mirrors SidebarNew adminNavigation)
  const adminNavigation = [
    { type: 'item', label: 'Dashboard', icon: LayoutDashboard, to: '/dashboard', permission: 'view_dashboard' },
    
    {
      type: 'expandable',
      label: 'Vendors',
      icon: Store,
      key: 'vendors',
      permission: 'manage_vendors',
      children: [
        { to: '/vendors', label: 'All Vendors', permission: 'manage_vendors' },
        { to: '/admin/vendor-compliance', label: 'Compliance Review', permission: 'manage_vendors' },
        { to: '/admin/payouts', label: 'Vendor Payouts', permission: 'manage_payouts' },
        { to: '/admin/payout-tax-logic', label: 'Payout & Tax Logic', permission: 'manage_settings' },
        { to: '/vendor-commissions', label: 'Vendor Commissions', permission: 'manage_vendors' },
        // DEPRECATED: Commission Settings removed - managed in Subscription Plans
        { to: '/admin/lead-pricing', label: 'Lead Pricing', permission: 'manage_vendors' },
        { to: '/subscription-plans', label: 'Subscription Plans', permission: 'manage_subscriptions' },
        { to: '/admin/campaigns', label: 'Vendor Campaigns', permission: 'manage_campaigns' }
      ]
    },
    {
      type: 'expandable',
      label: 'Listings',
      icon: Package,
      key: 'listings',
      permission: 'manage_listings',
      children: [
        { to: '/listings', label: 'All Listings', permission: 'manage_listings' },
        { to: '/admin/master-catalog', label: 'Master Catalog', permission: 'manage_listings' },
        { to: '/admin/approvals', label: 'Pending Approvals', permission: 'approve_listings' },
        { to: '/admin/product-option-templates', label: 'Option Templates', permission: 'manage_listings' }
      ]
    },
    {
      type: 'expandable',
      label: 'Orders',
      icon: ListOrdered,
      key: 'orders',
      permission: 'view_orders',
      children: [
        { to: '/orders', label: 'All Orders', permission: 'view_orders' },
        { to: '/orders/leads', label: 'All Leads', permission: 'view_orders' },
        { to: '/orders/shipping', label: 'Shipping', permission: 'view_shipping' }
      ]
    },
    {
      type: 'expandable',
      label: 'Customers',
      icon: Users,
      key: 'customers',
      permission: 'view_customers',
      children: [
        { to: '/customers', label: 'All Customers', permission: 'view_customers' },
        { to: '/customer-segments', label: 'Segments', permission: 'manage_segments' }
      ]
    },
    {
      type: 'expandable',
      label: 'Marketing',
      icon: TrendingUp,
      key: 'marketing',
      permission: 'manage_marketing',
      children: [
        { to: '/offers', label: 'Discounts & Offers', permission: 'manage_discounts' },
        { to: '/marketing/promotions', label: 'Promotions', permission: 'manage_promotions' },
        { to: '/marketing/ad-inventory', label: 'Ad Inventory', permission: 'manage_ads' },
        { to: '/marketing/campaigns', label: 'Ad Campaigns', permission: 'manage_campaigns' },
        { to: '/marketing/builder', label: 'Campaign Builder', permission: 'send_emails' }
      ]
    },
    { type: 'item', label: 'Analytics', icon: BarChart3, to: '/analytics', permission: 'view_analytics' },
    {
      type: 'expandable',
      label: 'CMS',
      icon: FileText,
      key: 'cms',
      permission: 'manage_content',
      children: [
        { to: '/categories', label: 'Categories', permission: 'manage_categories' },
        { to: '/tags', label: 'Tags', permission: 'manage_tags' },
        { to: '/collections', label: 'Collections', permission: 'manage_collections' },
        { to: '/media', label: 'Media Gallery', permission: 'manage_media' },
        { to: '/pages', label: 'Pages', permission: 'manage_pages' },
        { to: '/menus', label: 'Menus', permission: 'manage_menus' },
        { to: '/blog', label: 'Blog', permission: 'manage_blog' }
      ]
    },
    {
      type: 'expandable',
      label: 'Integrations',
      icon: Plug,
      key: 'integrations',
      permission: 'manage_integrations',
      superAdminOnly: true,  // Super admin only
      children: [
        { to: '/admin/shopify-sync', label: 'Shopify Sync', permission: 'manage_shopify' },
        { to: '/admin/integration-configs', label: 'API Configs', permission: 'manage_integrations' },
        { to: '/admin/webhook-manager', label: 'Webhooks', permission: 'manage_integrations' },
        { to: '/admin/sales-channels', label: 'Sales Channels', permission: 'manage_integrations' },
        { to: '/admin/vim', label: 'Ad Inventory (VIM)', permission: 'manage_settings' },
        { to: '/admin/storefront-demo', label: 'Storefront Demo', permission: 'manage_settings' }
      ]
    },
    { type: 'item', label: 'Training Academy', icon: GraduationCap, to: '/admin/training', permission: 'manage_settings' },
    { type: 'item', label: 'Notifications', icon: Bell, to: '/admin/notifications', permission: 'all' },
    { type: 'item', label: 'Team & Roles', icon: Users, to: '/admin/team', permission: 'manage_settings', superAdminOnly: true },
    { type: 'item', label: 'Settings', icon: SettingsIcon, to: '/settings', permission: 'view_settings', superAdminOnly: true },
    { type: 'item', label: 'Help & Support', icon: HelpCircle, to: '/help', permission: 'all' },
    { type: 'item', label: 'Messages', icon: MessageSquare, to: '/messaging', permission: 'all' }
  ];

  // Vendor navigation structure (mirrors SidebarNew vendorNavigation)
  const vendorNavigation = [
    { type: 'item', label: 'Dashboard', icon: LayoutDashboard, to: '/dashboard', permission: 'all' },
    
    // DAILY OPERATIONS
    { type: 'section', label: 'Daily Operations' },
    {
      type: 'expandable',
      label: 'Orders',
      icon: ListOrdered,
      key: 'orders',
      permission: 'all',
      children: [
        { to: '/orders', label: 'All Orders', permission: 'all' },
        { to: '/orders/leads', label: 'Leads & Inquiries', permission: 'all' },
        { to: '/vendor/requests', label: 'Customer Requests', permission: 'all', badge: '2' },
        { to: '/orders/shipping', label: 'Shipments', permission: 'all' },
        { to: '/vendor/tax-invoices', label: 'Tax Invoices', permission: 'all' }
      ]
    },
    { type: 'item', label: 'Bookings', icon: Calendar, to: '/bookings', permission: 'all' },
    { type: 'item', label: 'Subscribers', icon: Users, to: '/vendor/subscribers', permission: 'all' },
    { type: 'item', label: 'Payouts & Finance', icon: Wallet, to: '/vendor/payouts', permission: 'all' },
    
    // CATALOG
    { type: 'section', label: 'Catalog' },
    { type: 'item', label: 'Listings', icon: Package, to: '/listings', permission: 'all' },
    { type: 'item', label: 'Reviews', icon: Star, to: '/listings/reviews', permission: 'all' },
    { type: 'item', label: 'Inventory', icon: ClipboardList, to: '/orders/inventory', permission: 'all' },
    { type: 'item', label: 'Import Catalog', icon: Import, to: '/catalogue-pull', permission: 'all' },
    { type: 'item', label: 'Media Gallery', icon: Image, to: '/media', permission: 'all' },
    
    // GROW
    { type: 'section', label: 'Grow' },
    { type: 'item', label: 'Analytics', icon: PieChart, to: '/analytics', permission: 'all' },
    { type: 'item', label: 'Discounts', icon: Tag, to: '/offers', permission: 'all' },
    {
      type: 'expandable',
      label: 'Marketing',
      icon: Megaphone,
      key: 'marketing',
      permission: 'all',
      children: [
        { to: '/vendor/advertising', label: 'Advertising', permission: 'all' },
        { to: '/promotions', label: 'Promotions', permission: 'all' },
        { to: '/marketing/builder', label: 'Campaign Builder', permission: 'all' }
      ]
    },
    {
      type: 'expandable',
      label: 'Customers',
      icon: Users,
      key: 'customers',
      permission: 'all',
      children: [
        { to: '/customers', label: 'All Customers', permission: 'all' },
        { to: '/customer-segments', label: 'Segments', permission: 'all' }
      ]
    },
    
    // LEARN
    { type: 'section', label: 'Learn' },
    { type: 'item', label: 'Training Academy', icon: GraduationCap, to: '/vendor/training', permission: 'all', highlight: true },
    { type: 'item', label: 'Help & Support', icon: HelpCircle, to: '/help', permission: 'all' },
    
    // SETTINGS
    { type: 'section', label: 'Settings' },
    {
      type: 'expandable',
      label: 'Store Setup',
      icon: Store,
      key: 'myBusiness',
      permission: 'all',
      children: [
        { to: '/vendor/store-profile', label: 'Store Profile', permission: 'all' },
        { to: '/vendor/locations', label: 'Locations', permission: 'all' },
        { to: '/vendor/compliance', label: 'Compliance & KYC', permission: 'all' },
        { to: '/settings/shipping', label: 'Shipping Setup', permission: 'all' },
        { to: '/vendor/operational', label: 'Operations', permission: 'all' }
      ]
    },
    { type: 'item', label: 'Security', icon: Shield, to: '/vendor/security', permission: 'all' },
    { type: 'item', label: 'Billing', icon: CreditCard, to: '/vendor/billing', permission: 'all' },
    { type: 'item', label: 'Subscription', icon: Tag, to: '/vendor/subscription-plans', permission: 'all' },
    { type: 'item', label: 'Notifications', icon: Bell, to: '/vendor/notifications', permission: 'all' },
    { type: 'item', label: 'Team & Roles', icon: Users, to: '/users-roles', permission: 'all' },
    { type: 'item', label: 'Messages', icon: MessageSquare, to: '/messaging', permission: 'all' }
  ];

  // Select navigation based on user role
  const fullNavigation = isAdmin() ? adminNavigation : vendorNavigation;

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMenuOpen(false);
  };

  const toggleSection = (key) => {
    setExpandedSection(current => current === key ? null : key);
  };

  const renderNavItem = (item, index) => {
    // Hide superAdminOnly items from non-super admins
    if (item.superAdminOnly && !isSuperAdmin) {
      return null;
    }

    if (item.type === 'section') {
      return (
        <div key={`section-${index}`} className="pt-6 pb-2 px-4 first:pt-2">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            {item.label}
          </span>
        </div>
      );
    }

    if (item.type === 'expandable') {
      const Icon = item.icon;
      const isExpanded = expandedSection === item.key;
      const hasActiveChild = item.children?.some(child => isActive(child.to));

      return (
        <PermissionGate key={item.key} permission={item.permission}>
          <div className="px-2">
            <button
              onClick={() => toggleSection(item.key)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                hasActiveChild ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <Icon className="h-5 w-5 text-gray-500" />
                <span className="font-medium text-gray-900">{item.label}</span>
              </div>
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400" />
              )}
            </button>

            {isExpanded && (
              <div className="ml-4 pl-4 border-l border-gray-200 mt-1 space-y-1">
                {item.children?.map((child) => (
                  <PermissionGate key={child.to} permission={child.permission}>
                    <Link
                      to={child.to}
                      onClick={() => setIsMenuOpen(false)}
                      className={`flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors ${
                        isActive(child.to)
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-sm">{child.label}</span>
                      {child.badge && (
                        <Badge className="bg-red-500 text-white text-xs px-1.5 py-0.5">
                          {child.badge}
                        </Badge>
                      )}
                    </Link>
                  </PermissionGate>
                ))}
              </div>
            )}
          </div>
        </PermissionGate>
      );
    }

    // Regular item
    const Icon = item.icon;
    return (
      <PermissionGate key={item.to} permission={item.permission}>
        <div className="px-2">
          <Link
            to={item.to}
            onClick={() => setIsMenuOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActive(item.to)
                ? 'bg-gray-900 text-white'
                : item.highlight
                ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Icon className={`h-5 w-5 ${isActive(item.to) ? 'text-white' : item.highlight ? 'text-blue-600' : 'text-gray-500'}`} />
            <span className="font-medium">{item.label}</span>
          </Link>
        </div>
      </PermissionGate>
    );
  };

  return (
    <>
      {/* Full Screen Menu Overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-[100] bg-white flex flex-col">
          {/* Menu Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">A</span>
              </div>
              <span className="text-lg font-bold">Aniyor</span>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setIsMenuOpen(false)}>
              <X className="h-6 w-6" />
            </Button>
          </div>

          {/* User Info */}
          <div className="px-4 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-gray-600 font-medium">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{user?.name || 'User'}</p>
                <p className="text-sm text-gray-500">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation Items - Scrollable */}
          <nav className="flex-1 overflow-y-auto">
            <div className="pb-20">
              {fullNavigation.map((item, index) => renderNavItem(item, index))}
            </div>
          </nav>

          {/* Logout Button - Fixed at bottom */}
          <div className="flex-shrink-0 p-4 bg-white border-t border-gray-200">
            <Button
              variant="outline"
              className="w-full justify-center text-red-600 border-red-200 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-gray-200 z-[60] lg:hidden">
        <div className="flex items-center justify-around h-full px-2">
          {bottomNavItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center flex-1 h-full relative ${
                  active ? 'text-blue-600' : 'text-gray-500'
                }`}
              >
                <item.icon className={`h-5 w-5 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
                <span className="text-[10px] mt-1 font-medium">{item.label}</span>
                {active && (
                  <div className="absolute bottom-0 w-10 h-0.5 bg-blue-600 rounded-t-full" />
                )}
              </Link>
            );
          })}

          {/* Hamburger Menu Button (5th item) */}
          <button
            onClick={() => setIsMenuOpen(true)}
            className={`flex flex-col items-center justify-center flex-1 h-full ${
              isMenuOpen ? 'text-blue-600' : 'text-gray-500'
            }`}
          >
            <Menu className="h-5 w-5 text-gray-400" />
            <span className="text-[10px] mt-1 font-medium">Menu</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default MobileNavigation;
